import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet, Route } from 'react-router';
import { FeatureFlagged } from '#components/patterns/routing/FeatureFlagged.tsx';
import {
  AddAPIKeyPage,
  AddJoinKeyPage,
  AddTOTPModal,
  AddWebAuthnModal,
  AdminsPage,
  APIKeyDetailPage,
  APIKeysPage,
  DeleteTOTPModal,
  DeleteWebAuthnModal,
  EditTOTPModal,
  EditWebAuthnModal,
  JoinKeyDetailPage,
  JoinKeysPage,
  RenameOrgModal,
  UserPage,
} from '#route-bundles/authenticated.tsx';
import { NotFound } from '#screens/404/NotFound.tsx';
import { fallbackRenderNotFound } from '#screens/errors/fallbackRenderNotFound.tsx';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { IsMFAUser } from '#screens/settings/IsMFAUser.tsx';
import { fromArray, page, Titled } from '#util/title.ts';
import { billingRootRoute } from './billing/BillingRoot.tsx';
import { networksRootRoute } from './networks/NetworksRoot.tsx';
import { ssoRootRoute } from './sso/SSORoot.tsx';

/**
 * {@link settingsRootRoute} contains all of the routing config for the /settings path
 */
export const settingsRootRoute = (
  <Route
    element={
      <Titled title={() => page('Settings')}>
        <Suspense fallback={<LoadingSpinner />}>
          <Outlet />
        </Suspense>
      </Titled>
    }
  >
    <Route
      path="user"
      element={
        <IsMFAUser>
          <UserPage />
          <Outlet />
        </IsMFAUser>
      }
    >
      <Route path="totps/add" element={<AddTOTPModal />} />
      <Route path="totps/:totpId/edit" element={<EditTOTPModal />} />
      <Route path="totps/:totpId/delete" element={<DeleteTOTPModal />} />
      <Route path="passkeys/add" element={<AddWebAuthnModal />} />
      <Route path="passkeys/:webauthnId/edit" element={<EditWebAuthnModal />} />
      <Route path="passkeys/:webauthnId/delete" element={<DeleteWebAuthnModal />} />
      <Route path="organization/rename" element={<RenameOrgModal />} />
    </Route>
    <Route path="billing">{billingRootRoute}</Route>
    <Route path="networks">{networksRootRoute}</Route>
    <Route path="api-keys">
      <Route
        path="add"
        element={
          <Titled title={(title) => fromArray(['Add API key', title])}>
            <AddAPIKeyPage />
          </Titled>
        }
      />
      <Route
        index={true}
        element={
          <Titled title={(title) => fromArray(['API keys', title])}>
            <APIKeysPage />
          </Titled>
        }
      />
      <Route
        path=":apiKeyID"
        element={
          <Titled title={(title) => fromArray(['Edit API Key', title])}>
            <ErrorBoundary fallbackRender={fallbackRenderNotFound}>
              <APIKeyDetailPage />
            </ErrorBoundary>
          </Titled>
        }
      />
    </Route>
    <Route path="join-keys">
      <Route
        path="add"
        element={
          <Titled title={(title) => fromArray(['Add join key', title])}>
            <AddJoinKeyPage />
          </Titled>
        }
      />
      <Route
        index={true}
        element={
          <Titled title={(title) => fromArray(['Host join keys', title])}>
            <JoinKeysPage />
          </Titled>
        }
      />
      <Route
        path=":joinKeyID"
        element={
          <Titled title={(title) => fromArray(['Edit join key', title])}>
            <ErrorBoundary fallbackRender={fallbackRenderNotFound}>
              <JoinKeyDetailPage />
            </ErrorBoundary>
          </Titled>
        }
      />
    </Route>
    <Route
      path="admins"
      element={
        <IsMFAUser>
          <FeatureFlagged featureFlagIdent="ADMIN_USER_MANAGEMENT_LIMIT">
            <Titled title={(title) => fromArray(['Admins', title])}>
              <AdminsPage />
            </Titled>
          </FeatureFlagged>
        </IsMFAUser>
      }
    />
    <Route path="sso">{ssoRootRoute}</Route>
    <Route index={true} path="*" element={<NotFound />} />
  </Route>
);
